import axios from "axios";
import { logoutUser } from "./schoolSubscription";

/** Login session length issued by the API. Keep in step with JWT_EXPIRY_HOURS. */
export const SESSION_TIMEOUT_HOURS = 48;

const PUBLIC_PREFIXES = [
  "/landingpage",
  "/login",
  "/signup",
  "/forget",
  "/reset",
  "/change",
  "/google",
  "/authorize",
  "/verification",
  "/password",
  "/school",
];

export function isPublicAuthPath(path) {
  return PUBLIC_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix));
}

export function redirectToLoginIfNeeded() {
  logoutUser();
  const path = window.location.pathname;
  if (!isPublicAuthPath(path)) {
    window.location.assign("/landingpage");
  }
}

function isOurSessionRequest(url) {
  const value = String(url || "");
  if (/chatgpt|openai|azure|google_drive|christian_education/i.test(value)) return false;
  if (/\/login|\/forget|\/forgot|\/signup|\/verifyOTP|\/resentOTP|\/createUser/i.test(value)) {
    return false;
  }
  return true;
}

function requestHadAuth(headers) {
  if (!headers) return false;
  if (typeof headers.get === "function") {
    const value = headers.get("Authorization") || headers.get("authorization");
    if (value) return true;
  }
  return Boolean(headers.Authorization || headers.authorization);
}

let interceptorInstalled = false;

export function installSessionResetInterceptor() {
  if (interceptorInstalled) return;
  interceptorInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || "";
      if (status === 401 && requestHadAuth(error?.config?.headers) && isOurSessionRequest(url)) {
        redirectToLoginIfNeeded();
      }
      return Promise.reject(error);
    }
  );
}

let sessionCheck = null;

async function runServerSessionCheck() {
  const token = localStorage.getItem("access_token");
  if (!isAccessTokenActive(token)) return;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  if (!baseUrl) return;
  try {
    await axios.get(`${baseUrl}/user/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    if (error?.response?.status === 401) {
      redirectToLoginIfNeeded();
    }
  }
}

export function confirmServerSession() {
  if (sessionCheck) return sessionCheck;
  sessionCheck = runServerSessionCheck().finally(() => {
    sessionCheck = null;
  });
  return sessionCheck;
}

function decodeJwtPayload(token) {
  const parts = String(token || "").split(".");
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function isAccessTokenActive(token) {
  if (!token || token === "null" || token === "undefined" || String(token).trim() === "") {
    return false;
  }
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return false;
  return payload.exp * 1000 > Date.now();
}
